/**
 * Bound Record Store Hooks
 *
 * React hooks for accessing the centralized bound record store.
 * These hooks provide reactive access to local changes for bound records.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDiagramDocument } from '../../../contexts/DiagramDocumentContext';
import {
  getBoundRecordStateMap,
  getBoundRecordKey,
  updateBoundRecordChanges,
  discardBoundRecordChanges,
  getEffectiveRecord,
  type BoundRecordState,
} from '../stores/BoundRecordStore';

/**
 * Hook to access the bound record state for a specific record
 * Reactively updates when the record's local changes are modified
 */
export function useBoundRecordState(
  sourceId: string | undefined,
  recordId: string | undefined
): BoundRecordState | undefined {
  const { doc } = useDiagramDocument();
  const [state, setState] = useState<BoundRecordState | undefined>(undefined);

  useEffect(() => {
    if (!doc || !sourceId || !recordId) {
      setState(undefined);
      return;
    }

    const map = getBoundRecordStateMap(doc);
    const key = getBoundRecordKey(sourceId, recordId);

    // Get initial state
    setState(map.get(key));

    // Subscribe to changes
    const observer = () => {
      setState(map.get(key));
    };

    map.observe(observer);
    return () => map.unobserve(observer);
  }, [doc, sourceId, recordId]);

  return state;
}

/**
 * Hook to get the effective value for a specific field
 * Returns localChanges value if present, otherwise cachedRecord value
 */
export function useEffectiveFieldValue(
  sourceId: string | undefined,
  recordId: string | undefined,
  cachedRecord: Record<string, unknown> | undefined,
  field: string
): unknown {
  const state = useBoundRecordState(sourceId, recordId);

  return useMemo(() => {
    if (state?.localChanges && field in state.localChanges) {
      return state.localChanges[field];
    }
    return cachedRecord?.[field];
  }, [state, cachedRecord, field]);
}

/**
 * Hook to get all effective values for a record
 * Merges cachedRecord with localChanges
 */
export function useEffectiveRecord(
  sourceId: string | undefined,
  recordId: string | undefined,
  cachedRecord: Record<string, unknown> | undefined
): Record<string, unknown> {
  const state = useBoundRecordState(sourceId, recordId);

  return useMemo(() => {
    if (!cachedRecord) return {};
    if (!state?.localChanges || Object.keys(state.localChanges).length === 0) {
      return cachedRecord;
    }
    return {
      ...cachedRecord,
      ...state.localChanges,
    };
  }, [state, cachedRecord]);
}

/**
 * Hook to get all uncommitted records for a data source
 * Reactively updates when any record changes
 */
export function useUncommittedRecords(
  sourceId: string | undefined
): Array<{ recordId: string; state: BoundRecordState }> {
  const { doc } = useDiagramDocument();
  const [records, setRecords] = useState<Array<{ recordId: string; state: BoundRecordState }>>([]);

  useEffect(() => {
    if (!doc || !sourceId) {
      setRecords([]);
      return;
    }

    const map = getBoundRecordStateMap(doc);
    const prefix = `${sourceId}:`;

    // Get initial state
    const updateRecords = () => {
      const results: Array<{ recordId: string; state: BoundRecordState }> = [];
      map.forEach((state, key) => {
        if (key.startsWith(prefix)) {
          const recordId = key.substring(prefix.length);
          results.push({ recordId, state });
        }
      });
      setRecords(results);
    };

    updateRecords();

    // Subscribe to changes
    map.observe(updateRecords);
    return () => map.unobserve(updateRecords);
  }, [doc, sourceId]);

  return records;
}

/**
 * Hook that returns a map of recordId -> BoundRecordState for efficient lookups
 */
export function useUncommittedRecordsMap(
  sourceId: string | undefined
): Map<string, BoundRecordState> {
  const records = useUncommittedRecords(sourceId);

  return useMemo(() => {
    const map = new Map<string, BoundRecordState>();
    for (const { recordId, state } of records) {
      map.set(recordId, state);
    }
    return map;
  }, [records]);
}

/**
 * Hook to check if a specific record has uncommitted changes
 */
export function useHasUncommittedChanges(
  sourceId: string | undefined,
  recordId: string | undefined
): boolean {
  const state = useBoundRecordState(sourceId, recordId);
  return state !== undefined && Object.keys(state.localChanges).length > 0;
}

/**
 * Hook to get count of uncommitted records
 */
export function useUncommittedCount(sourceId: string | undefined): number {
  const records = useUncommittedRecords(sourceId);
  return records.length;
}

/**
 * Hook that provides mutation functions for bound records
 */
export function useBoundRecordMutations(sourceId: string | undefined) {
  const { doc } = useDiagramDocument();

  const updateFields = useCallback(
    (
      recordId: string,
      fieldChanges: Record<string, unknown>,
      cachedRecord: Record<string, unknown>,
      userId: string
    ) => {
      if (!doc || !sourceId) return;
      updateBoundRecordChanges(doc, sourceId, recordId, fieldChanges, cachedRecord, userId);
    },
    [doc, sourceId]
  );

  const discardChanges = useCallback(
    (recordId: string) => {
      if (!doc || !sourceId) return;
      discardBoundRecordChanges(doc, sourceId, recordId);
    },
    [doc, sourceId]
  );

  const getEffective = useCallback(
    (recordId: string, cachedRecord: Record<string, unknown>): Record<string, unknown> => {
      if (!doc || !sourceId) return cachedRecord;
      return getEffectiveRecord(doc, sourceId, recordId, cachedRecord);
    },
    [doc, sourceId]
  );

  return {
    updateFields,
    discardChanges,
    getEffective,
  };
}

/**
 * Hook to get effective records for the repository panel
 * Returns a function that computes effective values for any record
 */
export function useEffectiveRecordGetter(sourceId: string | undefined) {
  const uncommittedMap = useUncommittedRecordsMap(sourceId);

  return useCallback(
    (recordId: string, cachedRecord: Record<string, unknown>): Record<string, unknown> => {
      const state = uncommittedMap.get(recordId);
      if (!state?.localChanges || Object.keys(state.localChanges).length === 0) {
        return cachedRecord;
      }
      return {
        ...cachedRecord,
        ...state.localChanges,
      };
    },
    [uncommittedMap]
  );
}

/**
 * Summary of uncommitted changes (compatible with old useUncommittedShapes API)
 */
export interface UncommittedSummary {
  /** Total count of records with uncommitted changes */
  totalCount: number;
  /** Count of new records not yet in repository */
  newRecordCount: number;
  /** Count of modified existing records */
  modifiedCount: number;
  /** Count of records with conflicts */
  conflictCount: number;
  /** List of uncommitted records with their state */
  records: Array<{ recordId: string; state: BoundRecordState; isNew: boolean; hasConflict: boolean }>;
  /** Whether there are any uncommitted changes */
  hasChanges: boolean;
  /** Whether there are any conflicts blocking commit */
  hasConflicts: boolean;
}

/**
 * Hook to get summary of uncommitted changes for a data source
 * This provides a compatible API to the old useUncommittedShapes hook
 * but reads from the centralized store
 */
export function useUncommittedSummary(sourceId: string | undefined): UncommittedSummary {
  const uncommittedRecords = useUncommittedRecords(sourceId);

  return useMemo(() => {
    let newRecordCount = 0;
    let modifiedCount = 0;
    let conflictCount = 0;

    const records: Array<{ recordId: string; state: BoundRecordState; isNew: boolean; hasConflict: boolean }> = [];

    for (const { recordId, state } of uncommittedRecords) {
      // Check for conflicts (when repository has newer changes)
      // For now, we don't track conflicts in the central store - that would require
      // comparing with the repository version. Mark as modified.
      const hasConflict = false; // TODO: implement conflict detection
      const isNew = recordId.startsWith('NEW-');

      if (hasConflict) {
        conflictCount++;
      } else if (isNew) {
        newRecordCount++;
      } else {
        modifiedCount++;
      }

      records.push({ recordId, state, isNew, hasConflict });
    }

    // Sort by changedAt (most recent first)
    records.sort((a, b) => {
      if (!a.state.changedAt && !b.state.changedAt) return 0;
      if (!a.state.changedAt) return 1;
      if (!b.state.changedAt) return -1;
      return b.state.changedAt.localeCompare(a.state.changedAt);
    });

    return {
      totalCount: records.length,
      newRecordCount,
      modifiedCount,
      conflictCount,
      records,
      hasChanges: records.length > 0,
      hasConflicts: conflictCount > 0,
    };
  }, [uncommittedRecords]);
}
